import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Smartphone, ShieldAlert, RefreshCw, Trash2, Download, Users, AlertTriangle, Check,
} from 'lucide-react';
import { useToast } from '../components/Toast/Toast';
import {
  isDeviceContactsAvailable, checkContactsPermission, requestContactsPermission,
  getDeviceContacts, getContactAccounts, deleteDeviceContacts,
  normalizeNumber, accountLabel, isSyncedAccount,
  DeviceContact, ContactAccount,
} from '../services/deviceContacts';

/**
 * Duplicate numbers in the phone's own contact book.
 *
 * With several Google accounts on one handset the same person is stored once
 * per account, so "11,000 contacts" is mostly the same few thousand people
 * repeated. Each copy is shown with the account it lives in, because which
 * copy is deleted decides which Google account loses it.
 *
 * Deleting is real and it syncs. Google keeps a deleted contact in its own bin
 * for thirty days, which is the safety net — but the CSV backup here is taken
 * before anything goes, because a bin is a poor place to discover a mistake.
 */

interface Copy extends DeviceContact {
  key: string;
}

interface Group {
  number: string;
  copies: Copy[];
}

const PhoneContacts: React.FC = () => {
  const { showSuccess, showError } = useToast();

  const [permission, setPermission] = useState<'unknown' | 'granted' | 'denied'>('unknown');
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [accounts, setAccounts] = useState<ContactAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<number, boolean>>({});
  const [keepAccount, setKeepAccount] = useState<string>('');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, accs] = await Promise.all([getDeviceContacts(), getContactAccounts()]);
      setContacts(list);
      setAccounts(accs.sort((a, b) => b.count - a.count));
      setPicked({});
      if (!keepAccount && accs.length) {
        // Default to the account holding the most, which is the one people
        // think of as "my contacts".
        setKeepAccount([...accs].sort((a, b) => b.count - a.count)[0].name || '');
      }
    } catch (e: any) {
      showError('Could not read contacts', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDeviceContactsAvailable) return;
    checkContactsPermission().then(granted => {
      setPermission(granted ? 'granted' : 'denied');
      if (granted) load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askPermission = async () => {
    const granted = await requestContactsPermission();
    setPermission(granted ? 'granted' : 'denied');
    if (granted) load();
  };

  // One group per subscriber number that more than one raw contact carries.
  const groups = useMemo(() => {
    const byNumber = new Map<string, Copy[]>();
    for (const c of contacts) {
      const seen = new Set<string>();
      for (const raw of c.numbers || []) {
        const n = normalizeNumber(raw);
        if (n.length < 7 || seen.has(n)) continue;   // a short code is not a person
        seen.add(n);
        if (!byNumber.has(n)) byNumber.set(n, []);
        byNumber.get(n)!.push({ ...c, key: `${n}:${c.rawId}` });
      }
    }
    const out: Group[] = [];
    byNumber.forEach((copies, number) => {
      if (copies.length > 1) out.push({ number, copies });
    });
    const q = query.trim().toLowerCase();
    return out
      .filter(g => !q || g.number.includes(q) || g.copies.some(c => (c.name || '').toLowerCase().includes(q)))
      .sort((a, b) => b.copies.length - a.copies.length || a.number.localeCompare(b.number));
  }, [contacts, query]);

  const totals = useMemo(() => {
    const pickedIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => Number(k));
    return {
      contacts: contacts.length,
      numbers: groups.length,
      extra: groups.reduce((t, g) => t + g.copies.length - 1, 0),
      picked: pickedIds.length,
      pickedIds,
    };
  }, [contacts, groups, picked]);

  /** Which copy of a group deserves to stay: the preferred account first, then
   *  one that actually has a name, then the oldest (lowest raw id). */
  const keeper = useCallback((g: Group): Copy => {
    const byPreference = [...g.copies].sort((a, b) => {
      const pa = (a.accountName === keepAccount ? 0 : 1) - (b.accountName === keepAccount ? 0 : 1);
      if (pa !== 0) return pa;
      const na = (a.name ? 0 : 1) - (b.name ? 0 : 1);
      if (na !== 0) return na;
      return a.rawId - b.rawId;
    });
    return byPreference[0];
  }, [keepAccount]);

  const tickExtras = (only?: Group) => {
    const next: Record<number, boolean> = only ? { ...picked } : {};
    for (const g of (only ? [only] : groups)) {
      const keep = keeper(g);
      for (const c of g.copies) next[c.rawId] = c.rawId !== keep.rawId;
    }
    setPicked(next);
  };

  const backup = () => {
    if (!contacts.length) { showError('Backup', 'Nothing to back up'); return; }
    const esc = (v: string) => `"${String(v || '').replace(/"/g, '""')}"`;
    const lines = ['Name,Number,Account,Account Type,Raw Id'];
    for (const c of contacts) {
      for (const n of c.numbers || []) {
        lines.push([esc(c.name), esc(n), esc(c.accountName), esc(c.accountType), c.rawId].join(','));
      }
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `Phone_Contacts_Backup_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    showSuccess('Backup saved', `${contacts.length} contacts written to a CSV`);
  };

  /**
   * Delete a given set of copies.
   *
   * The list is trimmed in place rather than re-read from the phone: working
   * through numbers one at a time means a delete every few seconds, and
   * re-reading eleven thousand contacts after each one would throw away the
   * scroll position and the ticks along with it.
   */
  const removeIds = async (ids: number[], label: string) => {
    if (!ids.length) return;

    const synced = contacts.filter(c => ids.includes(c.rawId) && isSyncedAccount(c));
    const warn = [
      `Delete ${ids.length} ${label} from this phone?`,
      synced.length
        ? `${synced.length} of them live in a synced account, so they will also disappear from that Google account. Google keeps them in its bin for 30 days.`
        : '',
      'Take the CSV backup first if you have not.',
    ].filter(Boolean).join('\n\n');
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(warn)) return;

    setWorking('0%');
    try {
      const res = await deleteDeviceContacts(ids, (done, total) => {
        setWorking(`${Math.round((done / total) * 100)}%`);
      });
      const failed = new Set(res.failed);
      const gone = new Set(ids.filter(id => !failed.has(id)));
      setContacts(list => list.filter(c => !gone.has(c.rawId)));
      setPicked(p => {
        const next = { ...p };
        gone.forEach(id => delete next[id]);
        return next;
      });
      showSuccess(
        'Deleted',
        `${res.deleted} removed${res.failed.length ? `, ${res.failed.length} could not be` : ''}`,
      );
    } catch (e: any) {
      showError('Could not delete', e?.message || 'Unknown error');
    } finally {
      setWorking(null);
    }
  };

  const remove = () => removeIds(totals.pickedIds, 'contact copies');

  /** One number, dealt with on its own: whatever is ticked in this group, or
   *  every copy but the keeper when nothing is ticked yet. */
  const removeGroup = (g: Group) => {
    const ticked = g.copies.filter(c => picked[c.rawId]).map(c => c.rawId);
    const keep = keeper(g);
    const ids = ticked.length ? ticked : g.copies.filter(c => c.rawId !== keep.rawId).map(c => c.rawId);
    return removeIds(ids, `copies of ${g.number}`);
  };

  if (!isDeviceContactsAvailable) {
    return (
      <div className="py-6 max-w-md mx-auto px-6 text-center">
        <Smartphone className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h2 className="text-base font-semibold text-gray-800">Phone Contacts is mobile only</h2>
        <p className="text-sm text-gray-500 mt-1">
          This screen reads the contact book stored on the handset, so it works only in the
          ABS Cloud Android app.
        </p>
      </div>
    );
  }

  if (permission !== 'granted') {
    return (
      <div className="py-6 max-w-md mx-auto px-6 text-center">
        <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-base font-semibold text-gray-800">Allow contacts access</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          Needed to read your phone book and remove the copies you pick. Nothing is uploaded to
          the server.
        </p>
        <button onClick={askPermission} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium">
          Allow access
        </button>
      </div>
    );
  }

  return (
    <div className="pb-24">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
        <div className="flex items-center gap-2 px-3 py-2">
          <Users className="h-5 w-5 text-blue-600" />
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-gray-800 leading-tight">Phone Contacts</h1>
            <p className="text-[11px] text-gray-500 truncate">
              {loading
                ? 'Reading the phone book…'
                : `${totals.contacts} entries · ${totals.numbers} numbers repeat · ${totals.extra} copies could go`}
            </p>
          </div>
          <button onClick={backup} className="p-2 rounded-lg border border-gray-200 text-gray-500" title="Save a CSV backup first">
            <Download className="h-4 w-4" />
          </button>
          <button onClick={load} disabled={loading} className="p-2 rounded-lg border border-gray-200 text-gray-500 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="px-3 pb-2">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search a number or a name"
            className="w-full px-3 py-2 text-sm bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-400"
          />
        </div>

        {accounts.length > 0 && (
          <div className="px-3 pb-2 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase text-gray-400">Keep the copy in</span>
            <select
              value={keepAccount}
              onChange={e => setKeepAccount(e.target.value)}
              className="flex-1 min-w-[150px] px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white"
            >
              {accounts.map(a => (
                <option key={a.type + a.name} value={a.name}>
                  {accountLabel(a.name || a.type ? { accountName: a.name, accountType: a.type } : {})} — {a.count}
                </option>
              ))}
            </select>
            <button
              onClick={() => tickExtras()}
              className="px-3 py-1.5 text-xs font-bold rounded border border-amber-300 text-amber-700"
            >
              Tick the rest
            </button>
          </div>
        )}
      </div>

      {/* Several Google accounts is exactly why this screen exists, and also the
          thing that makes a careless delete expensive. */}
      <div className="mx-3 mt-3 flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          Each line below is one copy, in one account. Deleting a copy in a Google account removes
          it from that account everywhere — Google keeps it in its bin for 30 days. Take the CSV
          backup (the arrow above) before a big clear-out.
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Reading {totals.contacts || ''} contacts…</div>
      ) : groups.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">
          {contacts.length ? 'No number is stored more than once.' : 'No contacts with a phone number.'}
        </div>
      ) : (
        <>
          {groups.slice(0, shown).map(g => {
            const keep = keeper(g);
            return (
              <div key={g.number} className="mx-3 mt-3 rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="font-mono font-bold text-gray-900 text-sm">{g.number}</span>
                  <span className="text-[11px] text-gray-500">{g.copies.length} copies</span>
                  <button onClick={() => tickExtras(g)} className="ml-auto text-[11px] font-bold text-blue-600">
                    Tick the rest
                  </button>
                  {/* This number on its own, for working down the list one at a
                      time instead of ticking everything and deleting in one go. */}
                  <button
                    onClick={() => removeGroup(g)}
                    disabled={!!working}
                    className="flex items-center gap-1 px-2 py-1 rounded border border-red-300 text-red-700 text-[11px] font-bold disabled:opacity-50"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </div>
                <div className="divide-y divide-gray-100">
                  {g.copies.map(c => (
                    // A div, not a label, so the row's own delete button does
                    // not also toggle the tick on its way through.
                    <div
                      key={c.key}
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${picked[c.rawId] ? 'bg-red-50' : 'bg-white'}`}
                    >
                      <label className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={!!picked[c.rawId]}
                          onChange={e => setPicked(p => ({ ...p, [c.rawId]: e.target.checked }))}
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-gray-800 truncate">
                            {c.name || <span className="text-gray-400">(no name)</span>}
                            {c.rawId === keep.rawId && (
                              <span className="ml-1 text-[10px] font-bold text-emerald-600 uppercase">
                                <Check className="inline h-3 w-3" /> keep
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-gray-500 truncate">
                            {accountLabel(c)}
                            {c.numbers.length > 1 ? ` · ${c.numbers.length} numbers` : ''}
                          </span>
                        </span>
                      </label>
                      {/* This one copy, and nothing else. */}
                      <button
                        onClick={() => removeIds([c.rawId], `copy of ${g.number}`)}
                        disabled={!!working}
                        className="p-1.5 rounded text-red-500 hover:bg-red-100 disabled:opacity-50"
                        aria-label={`Delete this copy in ${accountLabel(c)}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {shown < groups.length && (
            <div className="px-3 mt-3">
              <button
                onClick={() => setShown(s => s + 100)}
                className="w-full py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600"
              >
                Show more ({groups.length - shown} left)
              </button>
            </div>
          )}
        </>
      )}

      {totals.picked > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-3 py-2.5 flex items-center gap-2 z-50">
          <span className="text-sm font-bold text-gray-800">{totals.picked} selected</span>
          <button onClick={() => setPicked({})} className="text-xs font-bold text-gray-500">Clear</button>
          <button
            onClick={remove}
            disabled={!!working}
            className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {working ? `Deleting ${working}` : 'Delete selected'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PhoneContacts;
